import {
  getArtifactTokenFromHash,
  getArtifactTokenFromSearch,
  getTokenFromHash,
  getTokenFromSearch,
  isArtifactPublicPath,
  isLifeStoryPublicPath
} from 'utils/publicRoutePrivacy';

export const SIGNUP_INVITE_QUERY_PARAM_KEYS = ['code', 'inviteId', 'inviteToken', 'token'] as const;

const PUBLIC_MESSAGE_PATH_PATTERNS = [/^\/public\/messages\/contacts\/([^/?#]+)\/?$/i, /^\/messages\/([^/?#]+)\/?$/i];

const normalizeConfiguredWebsiteUrl = (websiteUrl?: string) => {
  const normalizedWebsiteUrl = websiteUrl?.trim();
  if (!normalizedWebsiteUrl) {
    return undefined;
  }

  return /^[a-z][a-z\d+.-]*:\/\//i.test(normalizedWebsiteUrl)
    ? normalizedWebsiteUrl
    : `https://${normalizedWebsiteUrl}`;
};

const decodeInviteCode = (rawCode: string) => {
  try {
    return decodeURIComponent(rawCode);
  } catch {
    return rawCode;
  }
};

export const getInviteCodeFromSearchParams = (searchParams: URLSearchParams): string | undefined => {
  for (const key of SIGNUP_INVITE_QUERY_PARAM_KEYS) {
    const value = searchParams.get(key)?.trim();
    if (value) {
      return value;
    }
  }

  return undefined;
};

const getInviteCodeFromPathname = (pathname: string): string | undefined => {
  for (const pattern of PUBLIC_MESSAGE_PATH_PATTERNS) {
    const match = pathname.match(pattern);
    if (match?.[1]) {
      return decodeInviteCode(match[1]);
    }
  }

  if (isLifeStoryPublicPath(pathname)) {
    const segments = pathname.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1];
    if (lastSegment) {
      return decodeInviteCode(lastSegment);
    }
  }

  if (isArtifactPublicPath(pathname)) {
    const segments = pathname.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1];
    if (lastSegment) {
      return decodeInviteCode(lastSegment);
    }
  }

  return undefined;
};

export const getInviteCodeFromLocation = ({
  pathname,
  search,
  hash
}: {
  pathname: string;
  search?: string;
  hash?: string;
}): string | undefined => {
  const searchParams = new URLSearchParams(search || '');
  const searchCode = getInviteCodeFromSearchParams(searchParams);
  if (searchCode) {
    return searchCode;
  }

  const hashCode = getTokenFromHash(hash) || getArtifactTokenFromHash(hash);
  if (hashCode) {
    return hashCode;
  }

  const queryToken = getTokenFromSearch(search) || getArtifactTokenFromSearch(search);
  if (queryToken) {
    return queryToken;
  }

  return getInviteCodeFromPathname(pathname);
};

export const buildBrandedInviteSignupPath = (
  { pathname, search, hash }: { pathname: string; search?: string; hash?: string },
  options?: { inviteCode?: string | null }
): string => {
  const searchParams = new URLSearchParams(search || '');
  const hasExplicitInviteCode = options !== undefined;
  const normalizedExplicitInviteCode = options?.inviteCode?.trim();
  const inviteCode = normalizedExplicitInviteCode ?? getInviteCodeFromLocation({ pathname, search, hash });

  if (hasExplicitInviteCode) {
    for (const key of SIGNUP_INVITE_QUERY_PARAM_KEYS) {
      searchParams.delete(key);
    }

    if (normalizedExplicitInviteCode) {
      searchParams.set('code', normalizedExplicitInviteCode);
    }
  } else if (inviteCode && !searchParams.get('code')?.trim()) {
    searchParams.set('code', inviteCode);
  }

  const query = searchParams.toString();
  return query ? `/signup?${query}` : '/signup';
};

export const buildBrandedInviteSignupHref = (
  location: { pathname: string; search?: string; hash?: string },
  websiteUrl?: string,
  options?: { inviteCode?: string | null }
): string => {
  const signupPath = buildBrandedInviteSignupPath(location, options);
  const normalizedWebsiteUrl = normalizeConfiguredWebsiteUrl(websiteUrl);

  if (!normalizedWebsiteUrl) {
    return signupPath;
  }

  try {
    const configuredUrl = new URL(normalizedWebsiteUrl);
    if (configuredUrl.protocol !== 'http:' && configuredUrl.protocol !== 'https:') {
      return signupPath;
    }

    const signupUrl = new URL(signupPath, configuredUrl.origin);
    const mergedSearchParams = new URLSearchParams(configuredUrl.search);
    signupUrl.searchParams.forEach((value, key) => {
      mergedSearchParams.set(key, value);
    });

    configuredUrl.pathname = configuredUrl.pathname === '/' ? signupUrl.pathname : configuredUrl.pathname;
    configuredUrl.search = mergedSearchParams.toString();
    configuredUrl.hash = '';

    return configuredUrl.toString();
  } catch {
    return signupPath;
  }
};
